import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  MobileCard,
  MobileCardRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getAppIcon, getAppColor, getAppLetter, formatCurrency, formatDate, sortData } from "@/lib/utils";
import { Application } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import ViewApplicationDialog from "./view-application-dialog";
import EditApplicationDialog from "./edit-application-dialog";
import { DeleteConfirmationDialog } from "./delete-confirmation-dialog";
import { SortableTableHeader, SortDirection } from "@/components/ui/sortable-table-header";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { EmptyState } from "@/components/ui/empty-state";
import { Search, Eye, Edit, Trash, Trash2, AppWindow, Plus } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

type FilterOptions = {
  category: string;
};

type ApplicationTableProps = {
  onAddApplication: () => void;
  filters?: FilterOptions;
  onDeleteApplication: (id: number, name: string) => void;
};

export default function ApplicationTable({ onAddApplication, filters, onDeleteApplication }: ApplicationTableProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<string | null>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  
  // Bulk selection state
  const [selectedAppIds, setSelectedAppIds] = useState<Set<number>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const { data, isLoading, error } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });

  const applications: Application[] = Array.isArray(data) ? data : [];
  
  // Delete application mutation
  
  // Handle sorting
  const handleSort = (column: string) => {
    if (sortKey === column) {
      // Change direction if same column clicked
      setSortDirection(currentDirection => {
        if (currentDirection === "asc") return "desc";
        if (currentDirection === "desc") return null;
        return "asc";
      });
      
      // Clear sort when cycling through all states
      if (sortDirection === "desc") {
        setSortKey(null);
      }
    } else {
      // Set new sort column and default to ascending
      setSortKey(column);
      setSortDirection("asc");
    }
  };
  
  // Filter applications by search query and filter options
  const filteredApplications = applications.filter(app => {
    // Apply search query filter
    const matchesSearch = 
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (app.description && app.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (!matchesSearch) return false;
    
    // Apply filters from filter dialog if they exist
    if (filters) {
      // Category filter
      if (filters.category !== "all" && app.category.toLowerCase() !== filters.category.toLowerCase()) {
        return false;
      }
    }
    
    return true;
  });
  
  // Apply sorting
  const sortedApplications = sortData(filteredApplications, sortKey, sortDirection);
  
  // Calculate total pages for pagination
  const totalItems = sortedApplications.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  
  // Reset to page 1 if the current page is out of bounds or if filters change
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);
  
  // Reset to page 1 when filters or search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchQuery]);
  
  // Get paginated data
  const paginatedApplications = sortedApplications.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (applicationIds: number[]) => {
      const res = await apiRequest('/api/applications/bulk-delete', { 
        method: 'POST',
        body: { applicationIds }
      });
      return res;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/applications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      setSelectedAppIds(new Set());
      setBulkDeleteDialogOpen(false);
      toast({
        title: "Bulk delete completed",
        description: `Successfully deleted ${data.deleted.length} application(s). ${data.licensesTransferred > 0 ? `${data.licensesTransferred} licenses transferred to "Uncategorized".` : ''} ${data.errors.length > 0 ? `${data.errors.length} failed.` : ''}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete applications",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Bulk selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedApplications.map(app => app.id));
      setSelectedAppIds(allIds);
    } else {
      setSelectedAppIds(new Set());
    }
  };

  const handleSelectApp = (appId: number, checked: boolean) => {
    const newSelected = new Set(selectedAppIds);
    if (checked) {
      newSelected.add(appId);
    } else {
      newSelected.delete(appId);
    }
    setSelectedAppIds(newSelected);
  };

  const handleBulkDelete = () => {
    if (selectedAppIds.size === 0) return;
    bulkDeleteMutation.mutate(Array.from(selectedAppIds));
  };

  const isAdmin = user?.role === 'admin';
  const allSelected = paginatedApplications.length > 0 && paginatedApplications.every(app => selectedAppIds.has(app.id));
  const someSelected = paginatedApplications.some(app => selectedAppIds.has(app.id)) && !allSelected;

  return (
    <div>
      <ViewApplicationDialog 
        applicationId={selectedAppId}
        isOpen={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedAppId(null);
        }}
      />
      
      <EditApplicationDialog 
        applicationId={selectedAppId}
        isOpen={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAppId(null);
        }}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bulk Delete Applications</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedAppIds.size} selected application(s)? 
              Any associated licenses will be automatically transferred to "Uncategorized" to preserve your data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? "Deleting..." : `Delete ${selectedAppIds.size} Application(s)`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-semibold">Applications</CardTitle>
          <div className="flex space-x-2">
            {isAdmin && selectedAppIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteDialogOpen(true)}
                data-testid="button-bulk-delete-applications"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {selectedAppIds.size} Selected
              </Button>
            )}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </div>
              <Input
                type="text"
                placeholder="Search applications..."
                className="pl-10"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              {[...Array(5)].map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-error p-4 text-center">
              Error loading applications. Please try again.
            </div>
          ) : (
            <div className="rounded-md border">
              {/* Desktop Table */}
              <Table className="hidden md:table">
                <TableHeader>
                  <TableRow>
                    {isAdmin && (
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all applications"
                          data-testid="checkbox-select-all-applications"
                        />
                      </TableHead>
                    )}
                    <SortableTableHeader
                      column="name"
                      label="Application"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="min-w-[250px]"
                    />
                    <SortableTableHeader
                      column="category"
                      label="Category"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="hidden sm:table-cell min-w-[120px]"
                    />
                    <SortableTableHeader
                      column="addedAt"
                      label="Added On"
                      sortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      className="hidden md:table-cell min-w-[120px]"
                    />
                    <TableHead className="text-right min-w-[140px] sticky right-0 bg-white dark:bg-slate-900 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedApplications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 5 : 4} className="p-0">
                        <EmptyState
                          icon={<AppWindow className="h-10 w-10 text-slate-400" />}
                          title="No applications found"
                          description={
                            applications.length === 0
                              ? "Add the SaaS tools your company uses to start tracking licenses and costs."
                              : "Try adjusting your search or filters."
                          }
                          action={
                            applications.length === 0 ? (
                              <Button onClick={onAddApplication} variant="default">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Application
                              </Button>
                            ) : undefined
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedApplications.map((app) => (
                      <TableRow key={app.id}>
                        {isAdmin && (
                          <TableCell>
                            <Checkbox
                              checked={selectedAppIds.has(app.id)}
                              onCheckedChange={(checked) => handleSelectApp(app.id, checked as boolean)}
                              aria-label={`Select ${app.name}`}
                              data-testid={`checkbox-application-${app.id}`}
                            />
                          </TableCell>
                        )}
                        <TableCell priority="essential" truncate>
                          <div className="flex items-center space-x-3">
                            <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded ${getAppColor(app.name)} flex items-center justify-center flex-shrink-0`}>
                              <span className="text-sm sm:text-base font-semibold">{getAppLetter(app.name)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{app.name}</div>
                              <div className="text-sm text-muted-foreground truncate">{app.category}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell priority="key" truncate>
                          <span className="truncate block">{app.category}</span>
                        </TableCell>
                        <TableCell priority="optional" nowrap>
                          {formatDate(app.addedAt)}
                        </TableCell>
                        <TableCell priority="actions">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedAppId(app.id);
                                setViewDialogOpen(true);
                              }}
                              data-testid={`button-view-${app.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedAppId(app.id);
                                setEditDialogOpen(true);
                              }}
                              data-testid={`button-edit-${app.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => onDeleteApplication(app.id, app.name)}
                              data-testid={`button-delete-${app.id}`}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {/* Mobile Cards */}
              <div className="md:hidden space-y-4 p-4">
                {sortedApplications.length === 0 ? (
                  <EmptyState
                    icon={<AppWindow className="h-10 w-10 text-slate-400" />}
                    title="No applications found"
                    description={
                      applications.length === 0
                        ? "Add the SaaS tools your company uses to start tracking licenses and costs."
                        : "Try adjusting your search or filters."
                    }
                    action={
                      applications.length === 0 ? (
                        <Button onClick={onAddApplication} variant="default">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Application
                        </Button>
                      ) : undefined
                    }
                  />
                ) : (
                  paginatedApplications.map((app) => (
                    <MobileCard key={app.id}>
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`h-10 w-10 rounded ${getAppColor(app.name)} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-base font-semibold">{getAppLetter(app.name)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{app.name}</div>
                          <div className="text-sm text-muted-foreground truncate">{app.category}</div>
                        </div>
                      </div>
                      
                      <MobileCardRow label="Category">
                        {app.category}
                      </MobileCardRow>
                      
                      
                      <MobileCardRow label="Added On">
                        {formatDate(app.addedAt)}
                      </MobileCardRow>
                      
                      <div className="flex justify-end gap-2 pt-3 border-t">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedAppId(app.id);
                            setViewDialogOpen(true);
                          }}
                          data-testid={`button-view-mobile-${app.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedAppId(app.id);
                            setEditDialogOpen(true);
                          }}
                          data-testid={`button-edit-mobile-${app.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => onDeleteApplication(app.id, app.name)}
                          data-testid={`button-delete-mobile-${app.id}`}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </MobileCard>
                  ))
                )}
              </div>
              
              {/* Pagination */}
              {!isLoading && sortedApplications.length > 0 && (
                <div className="border-t px-4">
                  <DataTablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setCurrentPage(1); // Reset to first page when changing page size
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialogs */}
      <ViewApplicationDialog
        isOpen={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedAppId(null);
        }}
        applicationId={selectedAppId}
      />
      
      <EditApplicationDialog
        isOpen={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAppId(null);
        }}
        applicationId={selectedAppId}
      />
      
    </div>
  );
}
